import { AlertCircle, Info, RefreshCw } from "lucide-react";
import { useState } from "react";

import type { UseSourceWalletBalanceResult } from "@/modules/dashboard/crypto-swaps/hooks/useSourceWalletBalance";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import { TwoFactorActionModals } from "@/shared/components/overlays/TwoFactorActionModals";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { useCardCopTopUp, type CopTopUpStep } from "../../hooks/useCardCopTopUp";
import type { CardCopTopUpTerms } from "../../types/cardCopTopUp.types";
import type { CardCopTopUpTiming } from "../../types/cardFundingOptions.types";
import { CopTopUpAmountFields } from "./CopTopUpAmountFields";
import { useCopTopUpFormat } from "./copTopUpFormat";
import { CopTopUpResult } from "./CopTopUpResult";
import { CopTopUpReview } from "./CopTopUpReview";
import { CopTopUpTicket } from "./CopTopUpTicket";
import { CopTopUpTransit } from "./CopTopUpTransit";

const STEPS: CopTopUpStep[] = ["form", "review", "result"];

export interface CardCopTopUpDialogProps {
  open: boolean;
  cardId: string;
  last4: string;
  terms: CardCopTopUpTerms;
  /** Staff act on behalf of the card's organization; null for members. */
  organizationId: string | null;
  balance: UseSourceWalletBalanceResult;
  /** True when a top-up exists or may exist, so the page refreshes the card and the balances. */
  onClose: (placed: boolean) => void;
  /**
   * False when the card's organization has no card wallet: the credit cannot be withdrawn later, and the
   * review asks the reader to acknowledge it before confirming.
   */
  withdrawalsAllowed?: boolean;
  /** The stages of a load, for the estimate on the delivery screen. */
  timing?: CardCopTopUpTiming | null;
}

export function CardCopTopUpDialog({
  open,
  cardId,
  last4,
  terms,
  organizationId,
  balance,
  onClose,
  withdrawalsAllowed = true,
  timing = null,
}: CardCopTopUpDialogProps) {
  const { t } = useTranslations("cards");
  const format = useCopTopUpFormat();
  const flow = useCardCopTopUp({ cardId, terms, organizationId, balance });
  const placed = !!flow.topUp || flow.isUnresolved;
  const busy = flow.isSubmitting;
  // The code left and the challenge closed: the purchase is being placed, which takes a while.
  const sending = flow.step === "review" && flow.twoFactor.isRunning && !flow.twoFactor.challenge.open;
  const [acknowledged, setAcknowledged] = useState(false);
  const needsAcknowledgement = !withdrawalsAllowed;

  const close = () => {
    if (!busy) onClose(placed);
  };

  const stepLabels: Record<CopTopUpStep, string> = {
    form: t("cop_top_up_step_amount"),
    review: t("cop_top_up_step_review"),
    result: t("cop_top_up_step_result"),
  };
  const current = STEPS.indexOf(flow.step);

  const quoteFailure =
    flow.step === "form" && flow.quoteError && !flow.issue
      ? (flow.quoteError.serverMessages[0] ?? t("cop_top_up_unavailable_generic"))
      : null;

  return (
    <>
      <ColocaDialog
        open={open}
        onOpenChange={(next) => {
          if (!next) close();
        }}
        size="default"
        mobileFullscreen
        title={t("cop_top_up_title")}
        description={t("cop_top_up_description", { last4 })}
        toolbar={
          <ol className="flex items-center gap-2 text-xs" aria-label={t("cop_top_up_title")}>
            {STEPS.map((step, index) => (
              <li
                key={step}
                aria-current={index === current ? "step" : undefined}
                className={cn(
                  "flex items-center gap-2",
                  index === current ? "font-semibold text-foreground" : "text-muted-foreground",
                )}
              >
                <span
                  className={cn("h-1.5 w-6 rounded-full", index <= current ? "bg-primary" : "bg-border")}
                  aria-hidden="true"
                />
                {stepLabels[step]}
              </li>
            ))}
          </ol>
        }
        footer={
          flow.step === "form" ? (
            <>
              <ColocaButton variant="ghost" onClick={close}>
                {t("cop_top_up_cancel")}
              </ColocaButton>
              <ColocaButton variant="primary" onClick={flow.openReview} disabled={!flow.canReview}>
                {t("cop_top_up_review")}
              </ColocaButton>
            </>
          ) : flow.step === "review" ? (
            <>
              <ColocaButton variant="ghost" onClick={flow.backToForm} disabled={busy || flow.isUnresolved}>
                {t("cop_top_up_back")}
              </ColocaButton>
              {flow.isUnresolved ? (
                <ColocaButton
                  variant="primary"
                  icon={RefreshCw}
                  onClick={() => void flow.check()}
                  loading={flow.isChecking}
                >
                  {t("cop_top_up_check_unconfirmed")}
                </ColocaButton>
              ) : flow.isExpired ? (
                <ColocaButton variant="primary" onClick={flow.refreshPrice} loading={flow.isPricing}>
                  {t("cop_top_up_refresh_price")}
                </ColocaButton>
              ) : (
                <ColocaButton
                  variant="primary"
                  onClick={flow.submit}
                  loading={busy}
                  disabled={busy || flow.isPricing || !flow.quote || (needsAcknowledgement && !acknowledged)}
                >
                  {t("cop_top_up_confirm", { amount: format(flow.debit, "COP") })}
                </ColocaButton>
              )}
            </>
          ) : flow.vanished ? (
            <>
              <ColocaButton variant="ghost" onClick={close}>
                {t("cop_top_up_close")}
              </ColocaButton>
              <ColocaButton variant="primary" onClick={flow.startOver}>
                {t("cop_top_up_start_over")}
              </ColocaButton>
            </>
          ) : (
            <ColocaButton variant="primary" onClick={close}>
              {t("cop_top_up_close")}
            </ColocaButton>
          )
        }
      >
        {sending ? (
          <div role="status" className="flex flex-col items-center gap-4 py-6 text-center">
            <CopTopUpTransit network={terms.network} last4={last4} className="max-w-md" />
            <div className="flex flex-col gap-1.5">
              <h3 className="text-base font-semibold text-foreground">
                {t("cop_top_up_status_processing_title")}
              </h3>
              <p className="max-w-sm text-sm text-muted-foreground">
                {t("cop_top_up_status_processing_body")}
              </p>
            </div>
          </div>
        ) : flow.step === "result" && flow.vanished ? (
          <ColocaAlert variant="secondary" icon={Info} text={t("cop_top_up_vanished")} />
        ) : flow.step === "result" && flow.topUp ? (
          <CopTopUpResult topUp={flow.topUp} last4={last4} timing={timing} />
        ) : (
          <div className="flex flex-col gap-4">
            {flow.step === "form" && flow.checkOutcome === "not_placed" && (
              <ColocaAlert variant="secondary" icon={Info} text={t("cop_top_up_check_not_placed")} />
            )}
            {flow.step === "form" && <CopTopUpAmountFields flow={flow} disabled={busy} />}
            {flow.step === "form" && !flow.amount.trim() && (
              <p className="text-xs text-muted-foreground">{t("cop_top_up_type_amount")}</p>
            )}
            {quoteFailure && (
              <ColocaAlert
                variant="destructive"
                icon={AlertCircle}
                text={quoteFailure}
                actions={
                  <ColocaButton variant="ghost" size="sm" icon={RefreshCw} onClick={flow.refreshPrice}>
                    {t("cop_top_up_refresh_price")}
                  </ColocaButton>
                }
              />
            )}
            {(flow.quote || flow.isPricing) && (
              <CopTopUpTicket
                quote={flow.quote}
                credited={flow.credited}
                last4={last4}
                isPricing={flow.isPricing}
                clockOffsetMs={flow.clockOffsetMs}
                showTimer={!flow.isUnresolved}
              />
            )}
            {flow.step === "review" && (
              <CopTopUpReview
                flow={flow}
                acknowledgement={
                  needsAcknowledgement
                    ? {
                        checked: acknowledged,
                        onChange: setAcknowledged,
                        disabled: busy || flow.isUnresolved,
                      }
                    : undefined
                }
              />
            )}
            {flow.step === "review" && flow.isExpired && (
              <ColocaAlert variant="warning" icon={AlertCircle} text={t("cop_top_up_price_expired")} />
            )}
            {flow.submitError && (
              <ColocaAlert
                variant="destructive"
                icon={AlertCircle}
                text={
                  flow.submitError.kind === "unconfirmed"
                    ? t("cop_top_up_error_unconfirmed")
                    : (flow.submitError.messages[0] ?? t("cop_top_up_error_generic"))
                }
              />
            )}
            {flow.isUnresolved && flow.checkOutcome === "pending" && (
              <ColocaAlert variant="warning" icon={AlertCircle} text={t("cop_top_up_check_pending")} />
            )}
          </div>
        )}
      </ColocaDialog>

      <TwoFactorActionModals
        controller={flow.twoFactor}
        requiredDescription={t("cop_top_up_two_factor_required")}
        challengeDescription={t("cop_top_up_two_factor_description", {
          amount: format(flow.debit, "COP"),
          last4,
        })}
        // The sentence names the load, not the authenticator, so it reads right with a PIN too.
        challengePinDescription={t("cop_top_up_two_factor_description", {
          amount: format(flow.debit, "COP"),
          last4,
        })}
      />
    </>
  );
}
